export interface ImportSource { url: string; title: string; category: string }
export interface ImportRow extends ImportSource { have: boolean; on: boolean }

// Below the server's 300-source ceiling: each new source also schedules background work.
export const IMPORT_BATCH_SIZE = 50;
export const MAX_OPML_BYTES = 2 * 1024 * 1024;

function feedUrl(raw: string): string {
  const url = new URL(raw.trim());
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) throw new Error('Feed addresses must use HTTP or HTTPS without embedded credentials.');
  url.hash = '';
  return url.href;
}

/** One preview row per feed, in file order, retaining the nearest named category. */
export function parseOpml(xml: string, knownUrls: readonly string[]): ImportRow[] {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.querySelector('parsererror') || doc.documentElement.tagName.toLowerCase() !== 'opml' || !doc.querySelector('body')) throw new Error('Choose a valid OPML file.');
  const known = new Set(knownUrls.map((url) => { try { return feedUrl(url); } catch { return url; } }));
  const rows = new Map<string, ImportRow>();
  for (const outline of doc.querySelectorAll('outline[xmlUrl]')) {
    let url: string;
    try { url = feedUrl(outline.getAttribute('xmlUrl') ?? ''); }
    catch { throw new Error('An entry has an invalid feed address. Fix it in the OPML file and choose the file again.'); }
    if (rows.has(url)) continue;
    let category = 'Uncategorized';
    for (let parent = outline.parentElement; parent; parent = parent.parentElement) {
      const name = (parent.getAttribute('title') || parent.getAttribute('text') || '').trim();
      if (parent.tagName === 'outline' && !parent.hasAttribute('xmlUrl') && name) { category = name; break; }
    }
    const have = known.has(url);
    rows.set(url, { url, title: (outline.getAttribute('title') || outline.getAttribute('text') || '').trim() || url, category, have, on: !have });
  }
  if (!rows.size) throw new Error('This OPML file contains no feed addresses.');
  return [...rows.values()];
}

/** Acknowledged batches leave the selection; an uncertain batch can safely be retried. */
export async function importBatches(
  sources: readonly ImportSource[], send: (batch: ImportSource[]) => Promise<unknown>,
  accepted: (batch: ImportSource[], done: number) => void, signal: AbortSignal,
): Promise<boolean> {
  for (let at = 0; at < sources.length; at += IMPORT_BATCH_SIZE) {
    if (signal.aborted) return false;
    const batch = sources.slice(at, at + IMPORT_BATCH_SIZE);
    await send(batch);
    accepted(batch, at + batch.length);
  }
  return true; // all selected sources were acknowledged, including a final in-flight Stop
}
